import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { communityService } from '../services/api';
import { getImageUrl } from '../utils/imageUtils';
import { useAuth } from '../hooks/useAuth';
import { ACTIVE_THEME, getThemeVars } from '../themes/dashboardThemes';
import DashboardSidebar from '../components/DashboardSidebar';

// ── Toast ────────────────────────────────────────────────────────────────────

function Shell({ children, theme, contentClassName }) {
  return (
    <div
      className="dashboard-new font-body selection:bg-primary-container selection:text-on-primary-container h-[100dvh] overflow-hidden flex flex-col tablet-sm:flex-row"
      style={{ ...theme, backgroundColor: 'var(--chrome-bg)', color: 'var(--chrome-text)' }}
    >
      <DashboardSidebar activePath="/communities" />
      <main className="flex-1 h-[100dvh] overflow-y-auto hide-scrollbar cm-pb-mobile-nav">
        <div className={contentClassName || "px-6 tablet-sm:px-12 pt-8 pb-20 max-w-4xl mx-auto"}>
          {children}
        </div>
      </main>
    </div>
  );
}

function Toast({ message, type, onClose }) {
  if (!message) return null;
  return (
    <div className="fixed dashboard-floating-bottom left-1/2 -translate-x-1/2 z-[100] animate-fade-in">
      <div className={`flex items-center gap-3 px-5 py-3 rounded-xl shadow-lg text-sm font-bold max-w-md ${
        type === 'error' ? 'bg-error text-on-error'
        : type === 'warning' ? 'bg-secondary text-on-secondary'
        : type === 'info' ? 'bg-secondary-container text-on-secondary-container'
        : 'bg-primary text-on-primary'
      }`}>
        <span className="material-symbols-outlined text-base">
          {type === 'error' ? 'error' : type === 'warning' ? 'warning' : type === 'info' ? 'info' : 'check_circle'}
        </span>
        <span className="flex-1">{message}</span>
        <a onClick={onClose} className="ml-2 cursor-pointer opacity-70 hover:opacity-100 shrink-0">
          <span className="material-symbols-outlined text-base">close</span>
        </a>
      </div>
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────────────────

function EditCommunityNew() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useTranslation();
  const theme = getThemeVars(ACTIVE_THEME);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState({ message: '', type: 'success' });
  const [unauthorized, setUnauthorized] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    domain: '',
    allowDomainJoin: false,
    invitePolicy: 'ADMIN_ONLY',
    imageId: null,
  });

  // Original data to check for changes
  const [originalData, setOriginalData] = useState(null);
  const [originalCommunity, setOriginalCommunity] = useState(null);

  // Validation errors
  const [errors, setErrors] = useState({});

  // New image state
  const [newImage, setNewImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast({ message: '', type: 'success' }), 5000);
  };

  // ── Load community ────────────────────────────────────────────────────────

  useEffect(() => {
    loadCommunity();
  }, [id]);

  const loadCommunity = async () => {
    setLoading(true);
    try {
      const response = await communityService.getCommunityById(id);
      const community = response.data;

      // Check if user is the creator
      if (community.creatorId !== user?.id) {
        setUnauthorized(true);
        setLoading(false);
        return;
      }

      // Set form data
      const communityData = {
        name: community.name || '',
        description: community.description || '',
        domain: community.domain || '',
        allowDomainJoin: community.allowDomainJoin || false,
        invitePolicy: community.invitePolicy || 'ADMIN_ONLY',
        imageId: community.imageId || null,
      };

      setFormData(communityData);
      setOriginalData(communityData);
      setOriginalCommunity(community);

      // Set existing image preview if available
      if (community.imageId) {
        setImagePreview(getImageUrl(community.imageId));
      } else if (community.imageUrl) {
        setImagePreview(community.imageUrl);
      }
    } catch (err) {
      showToast(t('Error al cargar la comunidad'), 'error');
      setTimeout(() => navigate('/communities'), 2000);
    } finally {
      setLoading(false);
    }
  };

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleInputChange = (field) => (event) => {
    const value = field === 'allowDomainJoin' ? event.target.checked : event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        showToast(t('Por favor selecciona una imagen valida'), 'error');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        showToast(t('La imagen no debe superar 5MB'), 'error');
        return;
      }

      setNewImage(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setNewImage(null);
    setImagePreview(null);
    setFormData(prev => ({ ...prev, imageId: null }));
  };

  // ── Validation ────────────────────────────────────────────────────────────

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t('El nombre es requerido');
    } else if (formData.name.length < 3) {
      newErrors.name = t('El nombre debe tener al menos 3 caracteres');
    } else if (formData.name.length > 30) {
      newErrors.name = t('El nombre no puede exceder 30 caracteres');
    }

    if (!formData.description.trim()) {
      newErrors.description = t('La descripcion es requerida');
    } else if (formData.description.length < 10) {
      newErrors.description = t('La descripcion debe tener al menos 10 caracteres');
    } else if (formData.description.length > 200) {
      newErrors.description = t('La descripcion no puede exceder 200 caracteres');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const hasChanges = () => {
    if (newImage) return true;
    if (!originalData) return false;

    return Object.keys(formData).some(key =>
      formData[key] !== originalData[key]
    );
  };

  // ── Submit ────────────────────────────────────────────────────────────────

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showToast(t('Por favor corrige los errores en el formulario'), 'error');
      return;
    }

    if (!hasChanges()) {
      showToast(t('No hay cambios para guardar'), 'info');
      return;
    }

    setSubmitting(true);

    try {
      // Prepare update data — domain/privacy live in the admin panel now
      const updateData = {
        name: formData.name,
        description: formData.description,
      };

      // If there's a new image, upload it first
      if (newImage) {
        try {
          await communityService.uploadCommunityImage(id, newImage);
        } catch (err) {
          showToast(t('Error al subir la imagen, pero se actualizaran los demas datos'), 'warning');
        }
      }

      await communityService.updateCommunity(id, updateData);

      showToast(t('Comunidad actualizada exitosamente'));

      // Redirect after a short delay
      setTimeout(() => {
        navigate(`/communities/${id}`);
      }, 1500);
    } catch (err) {
      showToast(err.response?.data?.message || t('Error al actualizar la comunidad'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges()) {
      if (!window.confirm(t('Deseas descartar los cambios?'))) {
        return;
      }
    }
    navigate(`/communities/${id}`);
  };

  // ── Loading state ─────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Shell theme={theme} contentClassName="max-w-4xl min-h-[40dvh] flex items-center justify-center">
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
      </Shell>
    );
  }

  // ── Unauthorized state ────────────────────────────────────────────────────

  if (unauthorized) {
    return (
      <Shell theme={theme}>
            <div className="bg-error-container text-on-error-container rounded-xl p-5 flex items-center gap-3">
              <span className="material-symbols-outlined text-xl">block</span>
              <span className="text-sm font-medium">{t('No tienes permisos para editar esta comunidad')}</span>
            </div>
            <a
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 rounded-lg bg-surface-container-highest text-on-surface-variant text-sm font-bold cursor-pointer hover:bg-surface-variant transition-colors"
            >
              <span className="material-symbols-outlined text-base">arrow_back</span>
              {t('Volver')}
            </a>
      </Shell>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Shell theme={theme}>

          {/* ── Header ── */}
          <div className="flex items-center gap-4 mb-8">
            <a
              onClick={() => navigate(-1)}
              className="w-10 h-10 rounded-xl bg-surface-container-highest flex items-center justify-center cursor-pointer hover:bg-surface-variant transition-colors"
            >
              <span className="material-symbols-outlined text-on-surface-variant text-xl">arrow_back</span>
            </a>
            <div>
              <h1 className="text-2xl font-black text-on-surface tracking-tight">
                {t('Editar Comunidad')}
              </h1>
              {originalCommunity && (
                <p className="text-sm text-on-surface-variant mt-0.5">{originalCommunity.name}</p>
              )}
            </div>
          </div>


          {/* ── Main Card ── */}
          <div className="bg-surface-container-lowest rounded-xl border border-outline-variant/20 overflow-hidden">
            <form onSubmit={handleSubmit}>

              {/* ── Section: Basic Information ── */}
              <div className="px-8 tablet-sm:px-10 pt-8 pb-6">
                <div className="flex items-center gap-2 mb-6">
                  <span className="material-symbols-outlined text-lg text-primary">info</span>
                  <h2 className="text-lg font-bold text-on-surface">{t('Informacion Basica')}</h2>
                </div>

                <div className="space-y-6">
                  {/* Name — floating label */}
                  <div className="relative mt-2">
                    <input
                      type="text"
                      id="ec-name"
                      value={formData.name}
                      onChange={handleInputChange('name')}
                      maxLength={30}
                      className={`peer w-full border-2 rounded-lg px-3.5 pt-5 pb-2 text-base text-on-surface placeholder-transparent transition-colors focus:outline-none ${
                        errors.name ? 'border-error' : 'border-outline-variant/30 focus:border-primary'
                      }`}
                      style={{ backgroundColor: 'white' }}
                      placeholder={t('Nombre de la Comunidad')}
                    />
                    <label
                      htmlFor="ec-name"
                      className="absolute left-3 -top-2.5 bg-white px-1 text-sm text-on-surface-variant peer-focus:text-primary transition-colors"
                    >
                      {t('Nombre de la Comunidad')} *
                    </label>
                    {errors.name && <p className="text-xs text-error mt-1.5">{errors.name}</p>}
                  </div>

                  {/* Description — floating label */}
                  <div className="relative">
                    <textarea
                      id="ec-description"
                      value={formData.description}
                      onChange={handleInputChange('description')}
                      rows={4}
                      maxLength={200}
                      className={`peer w-full border-2 rounded-lg px-3.5 pt-5 pb-2 text-base text-on-surface placeholder-transparent transition-colors resize-none focus:outline-none ${
                        errors.description ? 'border-error' : 'border-outline-variant/30 focus:border-primary'
                      }`}
                      style={{ backgroundColor: 'white' }}
                      placeholder={t('Descripcion')}
                    />
                    <label
                      htmlFor="ec-description"
                      className="absolute left-3 -top-2.5 bg-white px-1 text-sm text-on-surface-variant peer-focus:text-primary transition-colors"
                    >
                      {t('Descripcion')} *
                    </label>
                    <div className="flex justify-between items-center mt-1">
                      {errors.description
                        ? <p className="text-xs text-error">{errors.description}</p>
                        : <span />
                      }
                      <p className="text-xs text-on-surface-variant">
                        {formData.description.length}/200
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Divider ── */}
              <div className="mx-8 tablet-sm:mx-10 border-t border-outline-variant/20" />

              {/* ── Section: Community Banner ── */}
              <div className="px-8 tablet-sm:px-10 pt-6 pb-6">
                <div className="flex items-center gap-2 mb-6">
                  <span className="material-symbols-outlined text-lg text-primary">image</span>
                  <h2 className="text-lg font-bold text-on-surface">{t('Banner de la Comunidad')}</h2>
                </div>

                {imagePreview ? (
                  <div className="max-w-2xl mx-auto">
                    <div className="rounded-xl overflow-hidden border border-outline-variant/20">
                      <img
                        src={imagePreview}
                        alt={t('Banner de la Comunidad')}
                        className="w-full h-60 object-cover bg-surface-container"
                      />
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-primary text-primary text-sm font-medium cursor-pointer hover:bg-primary/5 transition-colors">
                        <span className="material-symbols-outlined text-base">cloud_upload</span>
                        {t('Cambiar')}
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*"
                          onChange={handleImageChange}
                        />
                      </label>
                      <a
                        onClick={removeImage}
                        className="w-9 h-9 rounded-lg flex items-center justify-center text-error cursor-pointer hover:bg-error/10 transition-colors"
                      >
                        <span className="material-symbols-outlined text-xl">delete</span>
                      </a>
                    </div>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-outline-variant/40 rounded-xl p-8 text-center">
                    <span className="material-symbols-outlined text-5xl text-on-surface-variant/40 mb-3 block">cloud_upload</span>
                    <label className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-primary text-on-primary text-sm font-medium cursor-pointer hover:opacity-90 transition-opacity">
                      <span className="material-symbols-outlined text-base">cloud_upload</span>
                      {t('Subir Banner')}
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={handleImageChange}
                      />
                    </label>
                    <p className="text-xs text-on-surface-variant mt-3">
                      {t('JPG, PNG o GIF. Maximo 5MB. Recomendado: 1400x420px')}
                    </p>
                  </div>
                )}
              </div>

              {/* ── Divider ── */}
              <div className="mx-8 tablet-sm:mx-10 border-t border-outline-variant/20" />

              {/* ── Action Buttons ── */}
              <div className="px-8 tablet-sm:px-10 py-6 flex flex-col-reverse mobile-sm:flex-row mobile-sm:items-center mobile-sm:justify-end gap-3">
                <a
                  onClick={handleCancel}
                  className={`w-full mobile-sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg border-2 border-primary text-primary text-sm font-medium cursor-pointer hover:bg-primary/5 transition-colors ${
                    submitting ? 'opacity-50 pointer-events-none' : ''
                  }`}
                >
                  <span className="material-symbols-outlined text-base">close</span>
                  {t('Cancelar')}
                </a>
                <button
                  type="submit"
                  disabled={submitting || !hasChanges()}
                  className={`w-full mobile-sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg bg-primary text-on-primary text-sm font-medium transition-opacity ${
                    submitting || !hasChanges() ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90 cursor-pointer'
                  }`}
                >
                  <span className="material-symbols-outlined text-base">save</span>
                  {submitting ? t('Guardando...') : t('Guardar Cambios')}
                </button>
              </div>
            </form>
          </div>
      {/* Toast */}
      <Toast
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ message: '', type: 'success' })}
      />
    </Shell>
  );
}

export default EditCommunityNew;
